import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Translate as TranslateIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// ─────────────────────────────────────────────────────────────────────────────
// LINGUE DISPONIBILI
// ─────────────────────────────────────────────────────────────────────────────
export const AVAILABLE_LANGUAGES: { code: string; i18nKey: string }[] = [
    { code: 'EN', i18nKey: 'languages.english' },
    { code: 'FR', i18nKey: 'languages.french' },
    { code: 'ES', i18nKey: 'languages.spanish' },
    { code: 'IT', i18nKey: 'languages.italian' },
];

// ─────────────────────────────────────────────────────────────────────────────
// PROPS
// ─────────────────────────────────────────────────────────────────────────────
interface TranslationDialogProps {
    open: boolean;
    onClose: () => void;
    /** Se null → traduzione massiva di tutta la lista */
    itemTitle: string | null;
    /** Codici lingua già tradotti per questo item */
    alreadyTranslatedCodes?: string[];
    /** Callback traduzione. Passa il codice lingua scelto. */
    onConfirm: (languageCode: string) => Promise<void>;
    /**
     * Callback eliminazione traduzione.
     * Riceve il codice lingua da eliminare.
     */
    onDelete?: (languageCode: string) => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────────────────────
export const TranslationDialog: React.FC<TranslationDialogProps> = ({
                                                                        open,
                                                                        onClose,
                                                                        itemTitle,
                                                                        alreadyTranslatedCodes = [],
                                                                        onConfirm,
                                                                        onDelete,
                                                                    }) => {
    const { t } = useTranslation();

    const [selectedLang, setSelectedLang] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');

    // ── Stato dialog conferma eliminazione ──
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const isBulk = itemTitle === null;
    const isAlreadyTranslated = alreadyTranslatedCodes.includes(selectedLang);

    // ─────────────────────────────────────────────────────────────────────────
    // HANDLERS — traduzione
    // ─────────────────────────────────────────────────────────────────────────
    const handleLangChange = (e: SelectChangeEvent) => {
        setSelectedLang(e.target.value);
        setError('');
    };

    const handleConfirm = async () => {
        if (!selectedLang) {
            setError(t('translation.errorNoLanguage'));
            return;
        }
        setLoading(true);
        setError('');
        try {
            await onConfirm(selectedLang);
            handleReset();
            onClose();
        } catch {
            setError(t('translation.errorGeneric'));
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setSelectedLang('');
        setError('');
    };

    const handleClose = () => {
        if (loading || deleteLoading) return;
        handleReset();
        onClose();
    };

    // ─────────────────────────────────────────────────────────────────────────
    // HANDLERS — eliminazione
    // ─────────────────────────────────────────────────────────────────────────
    const handleDeleteClick = () => {
        setDeleteConfirmOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
    };

    const handleDeleteConfirm = async () => {
        if (!onDelete || !selectedLang) return;
        setDeleteLoading(true);
        setError('');
        try {
            /**
             * Implementa onDelete nel componente padre in questo modo:
             *
             * onDelete={async (languageCode) => {
             *     await TranslationAPI.Instance<TranslationAPI>()
             *         .deleteTranslation(itemId, sectionType, languageCode);
             * }}
             *
             * Endpoint suggerito:
             *   DELETE /api/translations/{sectionType}/{itemId}/{languageCode}
             */
            await onDelete(selectedLang);
            setDeleteConfirmOpen(false);
            handleReset();
            onClose();
        } catch {
            setError(t('translation.errorGeneric'));
            setDeleteConfirmOpen(false);
        } finally {
            setDeleteLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // HELPERS
    // ─────────────────────────────────────────────────────────────────────────
    const alreadyTranslated = AVAILABLE_LANGUAGES.filter(l =>
        alreadyTranslatedCodes.includes(l.code)
    );

    const selectedLangLabel = AVAILABLE_LANGUAGES.find(l => l.code === selectedLang)?.i18nKey ?? '';

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                    <TranslateIcon color="primary" />
                    {isBulk
                        ? t('translation.dialogTitleBulk')
                        : t('translation.dialogTitleSingle')}
                </DialogTitle>

                <DialogContent>
                    {/* ── Item su cui si traduce ── */}
                    {!isBulk && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('translation.dialogSubtitle')}: <strong>{itemTitle}</strong>
                        </Typography>
                    )}

                    {/* ── Disclaimer AI ── */}
                    <Alert
                        icon={<WarningIcon fontSize="inherit" />}
                        severity="warning"
                        variant="outlined"
                        sx={{ mb: 2 }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {t('translation.disclaimerTitle')}
                        </Typography>
                        <Typography variant="body2">
                            {t('translation.disclaimerBody')}
                        </Typography>
                    </Alert>

                    <Divider sx={{ my: 2 }} />

                    {/* ── Selezione lingua ── */}
                    <FormControl fullWidth size="small">
                        <InputLabel>{t('translation.selectLanguageLabel')}</InputLabel>
                        <Select
                            value={selectedLang}
                            label={t('translation.selectLanguageLabel')}
                            onChange={handleLangChange}
                            disabled={loading || deleteLoading}
                        >
                            {AVAILABLE_LANGUAGES.map(lang => (
                                <MenuItem key={lang.code} value={lang.code}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {t(lang.i18nKey)}
                                        {alreadyTranslatedCodes.includes(lang.code) && (
                                            <Chip
                                                label={t('translation.alreadyTranslatedChip')}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                sx={{ fontSize: '0.65rem', height: 18 }}
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* ── Alert lingua già tradotta ── */}
                    {isAlreadyTranslated && selectedLang && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {t('translation.alreadyTranslatedWarning')}
                        </Alert>
                    )}

                    {/* ── Alert errore ── */}
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* ── Lingue già tradotte ── */}
                    {alreadyTranslated.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                {t('translation.alreadyTranslatedLabel')}:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {alreadyTranslated.map(lang => (
                                    <Chip
                                        key={lang.code}
                                        label={t(lang.i18nKey)}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    {/* ── Bottone elimina — visibile solo se lingua già tradotta e onDelete fornita ── */}
                    {isAlreadyTranslated && onDelete && (
                        <Button
                            onClick={handleDeleteClick}
                            disabled={loading || deleteLoading}
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            sx={{ mr: 'auto' }} // ← spinto a sinistra
                        >
                            {t('translation.deleteButton')}
                        </Button>
                    )}

                    <Button
                        onClick={handleClose}
                        disabled={loading || deleteLoading}
                        variant="outlined"
                        color="inherit"
                    >
                        {t('standardUse.undoBtn')}
                    </Button>

                    <Button
                        onClick={handleConfirm}
                        disabled={loading || deleteLoading || !selectedLang || isAlreadyTranslated}
                        variant="contained"
                        color="primary"
                        startIcon={
                            loading
                                ? <CircularProgress size={16} color="inherit" />
                                : <TranslateIcon />
                        }
                    >
                        {loading
                            ? t('translation.translatingButton')
                            : t('translation.translateButton')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Dialog di conferma eliminazione ── */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {t('translation.deleteConfirmTitle')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('translation.deleteConfirmBody', {
                            lang: t(selectedLangLabel)
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={handleDeleteCancel}
                        disabled={deleteLoading}
                        variant="outlined"
                        color="inherit"
                    >
                        {t('standardUse.undoBtn')}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        disabled={deleteLoading}
                        variant="contained"
                        color="error"
                        startIcon={
                            deleteLoading
                                ? <CircularProgress size={16} color="inherit" />
                                : <DeleteIcon />
                        }
                    >
                        {deleteLoading
                            ? t('translation.deletingButton')
                            : t('translation.deleteConfirmButton')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};